/**
 * Resolving the linked modules against the ZCatalog while editing (#1717).
 *
 * A module picked through the object browser's text search is frozen into the
 * form data exactly as the search returned it — and that payload comes from
 * Solr, which does not carry `university_tokens`. The preview card would
 * therefore lose its school row until the page is saved and the backend
 * serializes the relation from the object again. Searching by UID goes to the
 * ZCatalog, whose brains carry the field.
 *
 * `uidsMissingSchool` decides what is worth fetching. An entry that already
 * carries `university_tokens` needs no lookup — on a saved page the backend
 * serialized the relation, and that one field is the only difference between
 * the two payloads, so nothing else could be recovered here either. The card
 * adapters accept a bare object as well as a list
 * (`helpers/DesignSystem/adapters`), hence anything truthy counts as present.
 * A module genuinely without a school stays in the list: Solr and the ZCatalog
 * both report `null` for it, so its provenance cannot be read off the payload
 * at all. It costs one request per open, which is the honest price for not
 * guessing.
 *
 * `moduleSubrequestKey` follows the linked modules, not the page: editing the
 * relations must not serve the previous result through the loaded/loading
 * guard. Spelling the UIDs out would make that a ~1000-character redux key on
 * a page with many modules, hence the FNV-1a fingerprint with the set size in
 * front of it. A fingerprint collision cannot render anything wrong: the hook
 * hands its items back keyed by `UID`, so a foreign result matches none of the
 * modules asked for and the card keeps its frozen form data.
 *
 * `EMPTY_SUBREQUEST` is a module-level constant because a fresh object per
 * render would give `useSelector` a new identity every time and spin up a
 * re-render loop.
 *
 * Two effects, deliberately separate. The first dispatches the search; `error`
 * belongs in its guard because the reducer resets `loading` and `loaded` on
 * failure and only clears `error` when a request for that key actually starts
 * — without it the editor would repeat the request forever. The second removes
 * the subrequest again when the module set changes or the block unmounts, so a
 * long editing session no longer leaves a redux slot per set behind. It must
 * not carry `loading`/`loaded`/`error` in its deps, or the cleanup would wipe
 * the slot the moment the request started. Because it purges the slot, a
 * failed search is retried once the editor navigates away from this set of
 * modules and back; while the set stays put, the card renders the frozen form
 * data without the school row — the behaviour from before this hook.
 */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resetSearchContent, searchContent } from '@plone/volto/actions';
// eslint-disable-next-line import/no-unresolved
import type { Content } from '@plone/types';
import type { NormalizableContent } from '@fhnw/helpers/DesignSystem/cardTransformers';
import type { ModuleEntryData } from '../groups';

interface SearchSubrequest {
  items: Content[];
  loading: boolean;
  loaded: boolean;
  error?: unknown;
}

const EMPTY_SUBREQUEST: SearchSubrequest = {
  items: [],
  loading: false,
  loaded: false,
  error: null,
};

const EMPTY_RESULT: Record<string, Content> = {};

const fingerprint = (value: string) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};

export const uidsMissingSchool = (entries: ModuleEntryData[]): string[] =>
  entries
    .filter((entry) => {
      const school = (entry?.module as NormalizableContent | undefined)
        ?.university_tokens;
      return Array.isArray(school) ? !school.length : !school;
    })
    .map((entry) => entry?.module?.UID)
    .filter((uid): uid is string => Boolean(uid));

export const moduleSubrequestKey = (uids: string[]): string => {
  const sorted = [...new Set(uids)].sort();
  return sorted.length
    ? `modules_${sorted.length}-${fingerprint(sorted.join('-'))}`
    : '';
};

export const useResolvedModules = (
  uids: string[],
  isEdit?: boolean,
): Record<string, Content> => {
  const dispatch = useDispatch();
  const sorted = [...new Set(uids)].sort();
  const subrequest = moduleSubrequestKey(sorted);

  const { items, loading, loaded, error } = useSelector(
    (state: { search?: { subrequests?: Record<string, SearchSubrequest> } }) =>
      state.search?.subrequests?.[subrequest] || EMPTY_SUBREQUEST,
  );

  useEffect(() => {
    if (!isEdit || !subrequest || loaded || loading || error) {
      return;
    }
    dispatch(
      searchContent(
        '/',
        {
          UID: sorted,
          metadata_fields: '_all',
          b_size: sorted.length,
        },
        subrequest,
      ) as any,
    );
  }, [dispatch, subrequest, isEdit, loaded, loading, error]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEdit || !subrequest) {
      return undefined;
    }
    return () => {
      dispatch(resetSearchContent(subrequest) as any);
    };
  }, [dispatch, subrequest, isEdit]);

  if (!isEdit || !loaded || !items.length) {
    return EMPTY_RESULT;
  }

  return Object.fromEntries(items.map((item) => [item.UID, item]));
};
